import { Box, Grid, Pagination, type SxProps, Typography } from '@mui/material';
import { ReportHeader } from '../ReportHeader';
import { useStyles } from '../styles/Report.styles';
import { ErrorBoundary } from 'react-error-boundary';
import { ReportErrorBoundary } from './ReportErrorBoundary';

import type { CutTableOptions } from '@/types/table';
import { CutReportTable } from './CutReportTable';
import { type SortDirection } from '@api/cut';
import { OrgFilters } from '@components/OrgFilters';
import type { FilterInput } from './today/TodayCutReport';
import { LoadingCard } from './BaseReport';

interface CutReportLayoutProps {
    rangeText?: string;
    tableHeight?: string;
    tableOptions?: CutTableOptions;
    filters?: FilterInput;
    hasData: boolean;
    map?: boolean;
    loading: boolean;
    title: string;
    description?: string;
    note?: string;
    splitReportElement?: React.ReactNode;
    sortOptions?: Record<'company' | 'location', SortDirection>;
    pageNum: number;
    numPages: number;
    setFilter?: (option: string, value: string, selectOne?: boolean) => void;
    setSortDirection?: (key: 'company' | 'location', value: SortDirection) => void;
    setPageNum: (value: number) => void;
}

export const CutReportLayout = ({
    loading,
    tableOptions,
    title,
    description,
    note,
    sortOptions,
    pageNum,
    filters,
    numPages,
    rangeText,
    setSortDirection,
    setFilter,
    setPageNum,
    sx,
    tableHeight = '50dvh !important',
}: CutReportLayoutProps & { sx?: SxProps }) => {
    const classes = useStyles();

    return (
        <ErrorBoundary fallback={<ReportErrorBoundary />}>
            <LoadingCard loading={loading} className={classes.root} sx={sx}>
                <Grid container direction='row' className={classes.wrapper}>
                    <Grid container direction={'row'} columnGap={2}>
                        <Grid item xs={12} md={4} lg={4} sm={12}>
                            <ReportHeader title={title} rangeText={rangeText} />

                            <Grid item xs={12} className={classes.description}>
                                <Typography>
                                    {description}
                                    <Box component='span' fontWeight='bold'>
                                        {note}
                                    </Box>
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid
                            item
                            display={'flex'}
                            xs={12}
                            md={8}
                            sm={12}
                            lg={8}
                            className={classes.actions}
                            rowGap={2}
                            columnGap={2}
                            flexWrap={'wrap'}>
                            <Grid
                                container
                                display={'flex'}
                                flexDirection={'row'}
                                alignItems={'flex-end'}
                                gap={1}
                                justifyContent={'flex-end'}
                                sx={{ paddingBottom: '10px' }}
                                rowGap={1}>
                                {Boolean(filters && setFilter) && (
                                    <OrgFilters
                                        handleClear={() => {
                                            /** empty function */
                                        }}
                                        orgFilters={filters!}
                                        setOrgFilters={setFilter!}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} justifyContent={'center'}>
                        {tableOptions && (
                            <CutReportTable
                                reportName={title}
                                tableOptions={tableOptions}
                                loading={false}
                                height={tableHeight}
                                sortOptions={sortOptions}
                                setSortDirection={setSortDirection as any}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid
                    item
                    xs={12}
                    justifyContent={'flex-end'}
                    display={'flex'}
                    alignItems={'flex-end'}
                    width={'100%'}
                    paddingTop={'10px'}>
                    <Pagination page={pageNum} onChange={(_, page) => setPageNum(page)} count={numPages} />
                </Grid>
            </LoadingCard>
        </ErrorBoundary>
    );
};
